import { useEffect } from "react";
import {
  PUBLIC_OG_DESCRIPTION,
  SEARCH_META_DESCRIPTION,
  SEARCH_KEYWORDS,
  SEARCH_TOPICS,
  SITE_NAME,
  SITE_TAGLINE,
  SITE_URL,
} from "../constants/seo";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
    return element;
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (key !== "tag") {
      element.setAttribute(key, value);
    }
  });
  return element;
}

function setMetaByName(name, content) {
  if (!content) return;
  upsertMeta(`meta[name="${name}"]`, { name, content });
}

function setMetaByProperty(property, content) {
  if (!content) return;
  upsertMeta(`meta[property="${property}"]`, { property, content });
}

function setCanonical(url) {
  if (!url) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function setJsonLd(data) {
  const id = "site-json-ld";
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export default function useSeo({
  title,
  metaDescription = SEARCH_META_DESCRIPTION,
  ogDescription = PUBLIC_OG_DESCRIPTION,
  keywords = SEARCH_KEYWORDS,
  path = "/",
  noindex = false,
  jsonLd = null,
  ogImage = "",
}) {
  useEffect(() => {
    const origin =
      (typeof window !== "undefined" && window.location.origin) ||
      SITE_URL.replace(/\/$/, "");
    const pageUrl = `${origin}${path}`;
    const fullTitle = title || `${SITE_NAME} - ${SITE_TAGLINE}`;
    const imageUrl = ogImage
      ? ogImage.startsWith("http")
        ? ogImage
        : `${origin}${ogImage.startsWith("/") ? ogImage : `/${ogImage}`}`
      : "";

    document.title = fullTitle;
    document.documentElement.lang = "zh-CN";

    setMetaByName("description", metaDescription);
    setMetaByName("keywords", keywords);
    setMetaByName("robots", noindex ? "noindex,nofollow" : "index,follow");

    const baiduVerify = process.env.REACT_APP_BAIDU_SITE_VERIFICATION;
    if (baiduVerify) {
      setMetaByName("baidu-site-verification", baiduVerify);
    }

    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:description", ogDescription);
    setMetaByProperty("og:url", pageUrl);
    setMetaByProperty("og:locale", "zh_CN");
    if (imageUrl) {
      setMetaByProperty("og:image", imageUrl);
    }

    upsertMeta('meta[itemprop="name"]', { itemprop: "name", content: fullTitle });
    upsertMeta('meta[itemprop="description"]', {
      itemprop: "description",
      content: ogDescription,
    });
    if (imageUrl) {
      upsertMeta('meta[itemprop="image"]', { itemprop: "image", content: imageUrl });
    }

    setMetaByName("twitter:card", imageUrl ? "summary_large_image" : "summary");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", ogDescription);
    if (imageUrl) {
      setMetaByName("twitter:image", imageUrl);
    }

    if (noindex) {
      const canonical = document.head.querySelector('link[rel="canonical"]');
      canonical?.remove();
    } else {
      setCanonical(pageUrl);
    }

    if (!noindex && path === "/" && !jsonLd) {
      setJsonLd({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            name: SITE_NAME,
            url: SITE_URL,
            description: metaDescription,
            inLanguage: "zh-CN",
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE_URL.replace(/\/$/, "")}/discover?keyword={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            description: metaDescription,
            knowsAbout: SEARCH_TOPICS,
          },
          {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "这个平台和其他婚恋网站有什么不同？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "平台配备专业婚恋咨询师，区别于仅做介绍的传统红娘，提供前期免费长期陪伴，从找对象、交往到结婚全程辅导。",
                },
              },
              {
                "@type": "Question",
                name: "如何开始找对象？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "免费注册并完善资料，完成AI测评后即可获得智能匹配推荐，并可预约咨询师陪伴服务。",
                },
              },
              {
                "@type": "Question",
                name: "咨询师陪伴是免费的吗？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "前期咨询陪伴服务免费，从相识、恋爱到结婚均可获得咨询师一对一辅导与支持。",
                },
              },
            ],
          },
        ],
      });
    } else if (!noindex && jsonLd) {
      setJsonLd(jsonLd);
    }
  }, [title, metaDescription, ogDescription, keywords, path, noindex, jsonLd, ogImage]);
}
